import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { uploadImage } from '../utils/cloudflare';
import DeliveryTemplates from './DeliveryTemplates';
import ContractPanel from './ContractPanel';
import DealRating from './DealRating';

const REVISION_REASONS = [
    'missing_hashtag', 'wrong_mention', 'quality', 'brief_mismatch', 'wrong_platform', 'late', 'other',
];

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const STATUS_STYLES = {
    in_progress: 'bg-sky-50 text-sky-700 border-sky-200',
    submitted: 'bg-amber-50 text-amber-700 border-amber-200',
    revision_requested: 'bg-orange-50 text-orange-700 border-orange-200',
    disputed: 'bg-red-50 text-red-700 border-red-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

export function StatusPill({ status, label }) {
    return (
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${STATUS_STYLES[status] || STATUS_STYLES.cancelled}`}>
            {label}
        </span>
    );
}

/** Live "N hours M minutes left" readout for the auto-approval window. */
function Countdown({ until, t }) {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(timer);
    }, []);

    if (!until) return null;
    const diff = new Date(until).getTime() - now;
    if (diff <= 0) return <span className="font-semibold text-emerald-600">{t('campaigns.autoApproveDue')}</span>;

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return <span className="font-semibold text-amber-600">{hours}s {minutes}dk</span>;
}

function DeliveryPreview({ delivery, t }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-bold text-slate-500">
                    {t('campaigns.deliveryRound')} {delivery.round}
                </span>
                <span className="text-[10px] text-slate-400">
                    {new Date(delivery.created_at).toLocaleDateString()}
                </span>
            </div>
            {delivery.link && (
                <a href={delivery.link} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-primary-700 font-semibold break-all hover:underline">
                    {delivery.link}
                </a>
            )}
            {delivery.note && <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{delivery.note}</p>}
            {delivery.files?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {delivery.files.map((file, index) => (
                        <a key={index} href={file.url} target="_blank" rel="noopener noreferrer">
                            <img src={file.url} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

function RevisionModal({ deal, onClose, onDone }) {
    const { t } = useTranslation();
    const [reasons, setReasons] = useState([]);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const toggle = (reason) => setReasons(prev =>
        prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]);

    const submit = async () => {
        if (!reasons.length && !note.trim()) {
            setError(t('campaigns.revisionReasonRequired'));
            return;
        }
        setSaving(true);
        try {
            const res = await api.post(`/api/campaigns/deals/${deal.id}/review/`, {
                action: 'revision', reasons, note,
            });
            if (res.status === 200) onDone(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || err.response?.data?.error || t('campaigns.saveError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9500] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="px-6 pt-6 pb-3">
                    <h3 className="text-lg font-black text-slate-800">{t('campaigns.revisionTitle')}</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t('campaigns.revisionHint')}</p>
                </div>

                <div className="px-6 space-y-2 max-h-[45vh] overflow-y-auto custom-scrollbar">
                    {REVISION_REASONS.map(reason => (
                        <label key={reason} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${reasons.includes(reason)
                            ? 'border-primary-300 bg-primary-50'
                            : 'border-slate-200 hover:bg-slate-50'}`}>
                            <input type="checkbox" checked={reasons.includes(reason)}
                                onChange={() => toggle(reason)}
                                className="mt-0.5 w-4 h-4 accent-[#20c9b5]" />
                            <span className="text-sm text-slate-700 leading-snug">{t(`campaigns.reason_${reason}`)}</span>
                        </label>
                    ))}
                    <textarea className="input min-h-[80px] resize-y text-sm" value={note}
                        placeholder={t('campaigns.revisionNotePlaceholder')}
                        onChange={(e) => setNote(e.target.value)} />
                    {error && <p className="text-xs text-red-600">{error}</p>}
                </div>

                <div className="px-6 py-4 flex gap-3">
                    <button onClick={onClose} className="btn-secondary flex-1 py-2.5 text-sm">{t('campaigns.cancel')}</button>
                    <button onClick={submit} disabled={saving}
                        className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2">
                        {saving && <span className="spinner-sm" />}
                        {t('campaigns.sendRevision')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Dispute Center — the campaign page turns into this once either side
 * escalates. Evidence chat is visible to the two parties and staff only;
 * the ruling buttons live in the admin panel.
 */
export function DisputeCenter({ deal, onUpdated }) {
    const { t } = useTranslation();
    const [dispute, setDispute] = useState(deal.dispute);
    const [text, setText] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);

    const send = async () => {
        if (!text.trim() && !attachments.length) return;
        setSending(true);
        try {
            const res = await api.post(`/api/campaigns/disputes/${dispute.id}/messages/`, {
                text, attachments,
            });
            if (res.status === 201) {
                setDispute(prev => ({ ...prev, messages: [...(prev.messages || []), res.data] }));
                setText('');
                setAttachments([]);
            }
        } catch (err) { /* surfaced by the interceptor */ }
        finally { setSending(false); }
    };

    const attach = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const url = await uploadImage(file);
            if (url) setAttachments(prev => [...prev, { type: 'photo', url }]);
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    const resolved = dispute.status === 'resolved';

    return (
        <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 overflow-hidden">
            {/* Header — what is frozen */}
            <div className="bg-red-600 text-white px-4 py-3">
                <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                        <p className="text-sm font-black">{t('campaigns.disputeSuspended')}</p>
                        <p className="text-xs text-white/80">
                            {t('campaigns.disputeLocked')}: <strong className="tabular-nums">{usd(deal.amount_usd)}</strong>
                        </p>
                    </div>
                </div>
            </div>

            {/* Ruling banner */}
            {resolved && (
                <div className="px-4 py-3 bg-white border-b border-red-100">
                    <p className="text-xs font-bold text-slate-700">{t('campaigns.disputeResolved')}</p>
                    <p className="text-sm text-slate-600 mt-0.5">
                        {t(`campaigns.resolution_${dispute.resolution}`)}
                        {dispute.resolution === 'split' && ` — ${dispute.creator_share}% / ${100 - dispute.creator_share}%`}
                    </p>
                    {dispute.admin_note && (
                        <p className="text-xs text-slate-500 mt-1 italic">"{dispute.admin_note}"</p>
                    )}
                </div>
            )}

            {/* Evidence thread */}
            <div className="px-4 py-3">
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                    {t('campaigns.evidenceTitle')}
                </p>
                <div className="space-y-2 max-h-72 overflow-y-auto custom-scrollbar pr-1">
                    {(dispute.messages || []).map(message => (
                        <div key={message.id} className={`rounded-xl p-2.5 border ${message.is_admin
                            ? 'bg-violet-50 border-violet-200'
                            : 'bg-white border-slate-200'}`}>
                            <div className="flex items-center gap-2 mb-1">
                                {message.sender?.profile_picture && (
                                    <img src={message.sender.profile_picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                                )}
                                <span className="text-[11px] font-bold text-slate-700">
                                    {message.is_admin ? t('campaigns.platformTeam') : message.sender?.username}
                                </span>
                                <span className="text-[10px] text-slate-400 ml-auto">
                                    {new Date(message.created_at).toLocaleString()}
                                </span>
                            </div>
                            {message.text && <p className="text-sm text-slate-700 leading-relaxed">{message.text}</p>}
                            {message.attachments?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                    {message.attachments.map((file, index) => (
                                        <a key={index} href={file.url} target="_blank" rel="noopener noreferrer">
                                            <img src={file.url} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {!resolved && (
                    <div className="mt-3">
                        {attachments.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {attachments.map((file, index) => (
                                    <div key={index} className="relative">
                                        <img src={file.url} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
                                        <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-slate-800 text-white text-[10px] leading-none">×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <input className="input flex-1 py-2 text-sm bg-white" value={text}
                                placeholder={t('campaigns.evidencePlaceholder')}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') send(); }} />
                            <label className="btn-secondary px-3 py-2 text-sm cursor-pointer flex items-center">
                                {uploading ? <span className="w-4 h-4 border-2 border-slate-300 border-t-primary-600 rounded-full animate-spin" /> : '📎'}
                                <input type="file" accept="image/*" className="hidden" onChange={attach} />
                            </label>
                            <button onClick={send} disabled={sending} className="btn-primary px-4 py-2 text-sm">
                                {t('campaigns.send')}
                            </button>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">{t('campaigns.evidenceHint')}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Order detail screen for one approved collaboration. Renders the creator's
 * submit form or the brand's review panel depending on who is looking, and
 * swaps to the Dispute Center once things escalate.
 *
 * `hideHeader` drops the campaign/counterparty/escrow strip at the top. A
 * brand's campaign card lists several deals side by side and needs it to tell
 * them apart; the creator's application row is already titled with that same
 * campaign and amount, and printing it twice is what made that tab hard to
 * read.
 */
function DealCenter({ deal, me, onUpdated, hideHeader = false }) {
    const { t } = useTranslation();
    const isCreator = deal.creator?.id === me.id;
    const [link, setLink] = useState('');
    const [note, setNote] = useState('');
    // Which saved template the note came from, if any — reported with the
    // delivery so the picker can order itself by what actually gets used.
    const [templateId, setTemplateId] = useState(null);
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [showRevision, setShowRevision] = useState(false);
    const [showDispute, setShowDispute] = useState(false);
    const [disputeReason, setDisputeReason] = useState('');

    const lastRevision = deal.revisions?.[deal.revisions.length - 1];

    const attach = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const url = await uploadImage(file);
            if (url) setFiles(prev => [...prev, { type: 'photo', url }]);
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    const submitDelivery = async () => {
        if (!link.trim() && !files.length) {
            setError(t('campaigns.deliveryRequired'));
            return;
        }
        setBusy(true);
        setError('');
        try {
            const res = await api.post(`/api/campaigns/deals/${deal.id}/deliver/`, {
                link, note, files, template_id: templateId,
            });
            if (res.status === 200) {
                setLink(''); setNote(''); setFiles([]); setTemplateId(null);
                onUpdated(res.data);
            }
        } catch (err) {
            setError(err.response?.data?.error || t('campaigns.saveError'));
        } finally { setBusy(false); }
    };

    const approve = async () => {
        setBusy(true);
        try {
            const res = await api.post(`/api/campaigns/deals/${deal.id}/review/`, { action: 'approve' });
            if (res.status === 200) onUpdated(res.data);
        } catch (err) {
            setError(err.response?.data?.error || t('campaigns.saveError'));
        } finally { setBusy(false); }
    };

    const openDispute = async () => {
        if (!disputeReason.trim()) return;
        setBusy(true);
        try {
            const res = await api.post(`/api/campaigns/deals/${deal.id}/dispute/`, { reason: disputeReason });
            if (res.status === 201) { setShowDispute(false); onUpdated(res.data); }
        } catch (err) {
            setError(err.response?.data?.detail || err.response?.data?.error || t('campaigns.saveError'));
        } finally { setBusy(false); }
    };

    // Escalated — the whole panel becomes the dispute center. The agreement
    // rides along above it: a frozen escrow is precisely the moment both
    // sides want to re-read what they signed.
    if (deal.status === 'disputed' && deal.dispute) {
        return (
            <div className="space-y-2">
                <ContractPanel deal={deal} contract={deal.contract} />
                <DisputeCenter deal={deal} onUpdated={onUpdated} />
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {/* Escrow header */}
            {!hideHeader && (
                <div className="px-4 py-3 bg-gradient-to-r from-primary-50 to-white border-b border-slate-100 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate">{deal.campaign_title}</p>
                        <p className="text-xs text-slate-500">
                            {isCreator ? deal.brand?.username : deal.creator?.username}
                        </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                        <p className="text-lg font-black text-slate-800 tabular-nums">{usd(deal.amount_usd)}</p>
                        <p className="text-[10px] font-semibold text-slate-400">
                            {t(`campaigns.escrow_${deal.escrow_status}`)}
                        </p>
                    </div>
                </div>
            )}

            <div className="p-4 space-y-3">
                {/* The agreement, next to the money it governs. */}
                <ContractPanel deal={deal} contract={deal.contract} />

                <div className="flex items-center gap-2 flex-wrap">
                    {/* The row that hides the header already carries this pill
                        on its own title line — the revision counter does not
                        appear there, so that part stays either way. */}
                    {!hideHeader && (
                        <StatusPill status={deal.status} label={t(`campaigns.dealStatus_${deal.status}`)} />
                    )}
                    <span className="text-[11px] text-slate-400">
                        {t('campaigns.revisionsUsed')}: {deal.revision_count}/{deal.revision_limit}
                    </span>
                </div>

                {/* Last revision feedback */}
                {lastRevision && deal.status === 'revision_requested' && (
                    <div className="rounded-xl bg-orange-50 border border-orange-200 p-3">
                        <p className="text-[11px] font-bold text-orange-700 mb-1.5">{t('campaigns.revisionRequested')}</p>
                        <ul className="space-y-0.5">
                            {(lastRevision.reasons || []).map(reason => (
                                <li key={reason} className="text-sm text-orange-800">• {t(`campaigns.reason_${reason}`)}</li>
                            ))}
                        </ul>
                        {lastRevision.note && <p className="text-sm text-orange-800 mt-1.5 italic">"{lastRevision.note}"</p>}
                    </div>
                )}

                {/* Delivery history */}
                {deal.deliveries?.length > 0 && (
                    <div className="space-y-2">
                        {deal.deliveries.map(delivery => (
                            <DeliveryPreview key={delivery.id} delivery={delivery} t={t} />
                        ))}
                    </div>
                )}

                {/* ── Creator: submit work ── */}
                {isCreator && ['in_progress', 'revision_requested'].includes(deal.status) && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                        <p className="text-[11px] font-bold text-slate-600">{t('campaigns.submitWorkTitle')}</p>
                        <input className="input bg-white text-sm" value={link} placeholder={t('campaigns.deliveryLinkPlaceholder')}
                            onChange={(e) => setLink(e.target.value)} />

                        <DeliveryTemplates currentNote={note}
                            onApply={(text, id) => { setNote(text); setTemplateId(id); }} />

                        {/* Tweaking the wording still counts as using the
                            template; wiping the note is what un-uses it. */}
                        <textarea className="input bg-white text-sm min-h-[60px] resize-y" value={note}
                            placeholder={t('campaigns.deliveryNotePlaceholder')}
                            onChange={(e) => {
                                setNote(e.target.value);
                                if (!e.target.value.trim()) setTemplateId(null);
                            }} />
                        {files.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {files.map((file, index) => (
                                    <div key={index} className="relative">
                                        <img src={file.url} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
                                        <button onClick={() => setFiles(prev => prev.filter((_, i) => i !== index))}
                                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-slate-800 text-white text-[10px] leading-none">×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <label className="btn-secondary px-3 py-2 text-sm cursor-pointer flex items-center">
                                {uploading ? <span className="w-4 h-4 border-2 border-slate-300 border-t-primary-600 rounded-full animate-spin" /> : '📎'}
                                <input type="file" accept="image/*" className="hidden" onChange={attach} />
                            </label>
                            <button onClick={submitDelivery} disabled={busy}
                                className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-2">
                                {busy && <span className="spinner-sm" />}
                                {t('campaigns.submitWork')}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Creator: waiting for review ── */}
                {isCreator && deal.status === 'submitted' && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
                        <p className="text-sm font-semibold text-amber-800">{t('campaigns.awaitingBrand')}</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                            {t('campaigns.timeLeft')}: <Countdown until={deal.auto_approve_at} t={t} />
                        </p>
                        <p className="text-[11px] text-amber-600/80 mt-1.5 leading-relaxed">{t('campaigns.autoApproveExplain')}</p>
                    </div>
                )}

                {/* ── Brand: review panel ── */}
                {!isCreator && deal.status === 'submitted' && (
                    <div className="space-y-2">
                        <p className="text-xs text-slate-500 text-center">
                            {t('campaigns.reviewWindow')}: <Countdown until={deal.auto_approve_at} t={t} />
                        </p>
                        <button onClick={approve} disabled={busy}
                            className="btn-success w-full py-3 text-base font-bold flex items-center justify-center gap-2">
                            {busy && <span className="spinner-sm" />}
                            {t('campaigns.approveAndPay')}
                        </button>
                        <button onClick={() => setShowRevision(true)}
                            className="w-full py-2 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition">
                            {t('campaigns.requestRevision')}
                        </button>
                        <p className="text-[11px] text-slate-400 text-center leading-relaxed">{t('campaigns.reviewHint')}</p>
                    </div>
                )}

                {/* ── Brand: waiting on the creator ── */}
                {!isCreator && ['in_progress', 'revision_requested'].includes(deal.status) && (
                    <p className="text-sm text-slate-500 text-center py-2">{t('campaigns.awaitingCreator')}</p>
                )}

                {/* ── Settled ── */}
                {['completed', 'cancelled'].includes(deal.status) && (
                    <>
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
                            <p className="text-sm font-bold text-emerald-800">
                                {deal.status === 'completed' ? t('campaigns.dealCompleted') : t('campaigns.dealCancelled')}
                            </p>
                            <p className="text-xs text-emerald-700 mt-0.5 tabular-nums">
                                {t('campaigns.creatorGot')} {usd(deal.creator_usd)} · {t('campaigns.brandGot')} {usd(deal.brand_usd)}
                            </p>
                        </div>

                        {/* The one moment both sides can answer for each other.
                            Right here, while the collaboration is still the
                            thing on screen. */}
                        <DealRating deal={deal} onUpdated={onUpdated} />
                    </>
                )}

                {/* ── Escalation ── */}
                {deal.can_dispute && !showDispute && (
                    <button onClick={() => setShowDispute(true)}
                        className="w-full py-2 rounded-xl text-xs font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition">
                        {t('campaigns.raiseDispute')}
                    </button>
                )}
                {!deal.can_dispute && ['submitted', 'in_progress'].includes(deal.status) && (
                    <p className="text-[11px] text-slate-400 text-center leading-relaxed">{t('campaigns.disputeLockedHint')}</p>
                )}

                {showDispute && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2">
                        <p className="text-[11px] font-bold text-red-700">{t('campaigns.disputeFormTitle')}</p>
                        <textarea className="input bg-white text-sm min-h-[70px] resize-y" value={disputeReason}
                            placeholder={t('campaigns.disputeReasonPlaceholder')}
                            onChange={(e) => setDisputeReason(e.target.value)} />
                        <div className="flex gap-2">
                            <button onClick={() => setShowDispute(false)} className="btn-secondary flex-1 py-2 text-sm">
                                {t('campaigns.cancel')}
                            </button>
                            <button onClick={openDispute} disabled={busy || !disputeReason.trim()}
                                className="btn-danger flex-1 py-2 text-sm">
                                {t('campaigns.startDispute')}
                            </button>
                        </div>
                    </div>
                )}

                {error && <p className="text-xs text-red-600 text-center">{error}</p>}
            </div>

            {showRevision && (
                <RevisionModal deal={deal} onClose={() => setShowRevision(false)}
                    onDone={(updated) => { setShowRevision(false); onUpdated(updated); }} />
            )}
        </div>
    );
}

export default DealCenter;
